import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  PartyPopper, 
  Crown, 
  Heart, 
  Cake,
  ArrowRight,
  Users,
  Calendar,
  MapPin,
  Star,
  Sparkles,
  Gift,
  Music,
  Camera
} from 'lucide-react';

const EventsPage = () => {
  const [selectedEventType, setSelectedEventType] = useState('all');

  const eventTypes = [
    { id: 'all', name: 'Tous les Événements', icon: PartyPopper },
    { id: 'wedding', name: 'Mariages', icon: Heart },
    { id: 'birthday', name: 'Anniversaires', icon: Cake },
    { id: 'corporate', name: 'Événements Corporate', icon: Crown },
    { id: 'family', name: 'Célébrations Familiales', icon: Users }
  ];

  const eventPortfolio = [
    {
      id: 1,
      type: 'wedding',
      title: "Mariage Féerique en Villa",
      subtitle: "Cérémonie et réception dans un cadre d'exception",
      image: "https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      guests: "50-150 invités",
      duration: "Week-end complet",
      location: "Villa avec jardins",
      price: "À partir de 25 000€",
      highlights: ["Décoration florale sur-mesure", "Chef étoilé", "Musiciens live", "Photographe professionnel"]
    },
    {
      id: 2,
      type: 'birthday',
      title: "Anniversaire Spectaculaire",
      subtitle: "Célébration inoubliable avec surprises",
      image: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      guests: "20-80 invités",
      duration: "Soirée complète",
      location: "Terrasse avec vue mer",
      price: "À partir de 8 000€",
      highlights: ["Thème personnalisé", "Animations surprises", "Gâteau d'exception", "Feux d'artifice privés"]
    },
    {
      id: 3,
      type: 'corporate',
      title: "Séminaire Luxury",
      subtitle: "Événement corporate dans cadre privilégié",
      image: "https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      guests: "15-50 participants",
      duration: "2-3 jours",
      location: "Villa avec espaces modulables",
      price: "À partir de 15 000€",
      highlights: ["Équipements hi-tech", "Team building exclusif", "Conférenciers experts", "Networking de prestige"]
    },
    {
      id: 4,
      type: 'family',
      title: "Réunion de Famille Royale",
      subtitle: "Rassemblement multi-générationnel",
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      guests: "30-100 personnes",
      duration: "Week-end",
      location: "Villa multi-générations",
      price: "À partir de 12 000€",
      highlights: ["Activités tous âges", "Espaces dédiés", "Cuisine traditionnelle", "Photographe familial"]
    }
  ];

  const services = [
    {
      icon: Music,
      title: "Animation & Spectacle",
      description: "DJ, orchestres, artistes, spectacles sur-mesure",
      details: ["DJ internationaux", "Orchestres classiques", "Artistes en live", "Spectacles chorégraphiés"]
    },
    {
      icon: Camera,
      title: "Captation Professionnelle",
      description: "Photo, vidéo, drone pour immortaliser l'événement",
      details: ["Photographes renommés", "Vidéastes créatifs", "Prises de vue drone", "Montage professionnel"]
    },
    {
      icon: Sparkles,
      title: "Décoration d'Exception",
      description: "Design floral, éclairage, décors thématiques",
      details: ["Architectes floraux", "Éclairage scénographique", "Mobilier de luxe", "Décors sur-mesure"]
    },
    {
      icon: Gift,
      title: "Services Premium",
      description: "Conciergerie, sécurité, transport VIP",
      details: ["Concierge dédié", "Sécurité discrète", "Chauffeurs privés", "Hébergement invités"]
    }
  ];

  const filteredEvents = selectedEventType === 'all' 
    ? eventPortfolio 
    : eventPortfolio.filter(event => event.type === selectedEventType);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <PartyPopper className="w-4 h-4 mr-2" />
            Événementiel de Prestige
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Événementiel Privé
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Créons ensemble des moments exceptionnels dans le cadre prestigieux de nos villas d'exception
          </p>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Organiser mon Événement
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Event Types Filter */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {eventTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <Button
                  key={type.id}
                  variant={selectedEventType === type.id ? 'default' : 'outline'}
                  onClick={() => setSelectedEventType(type.id)}
                  className={`px-6 py-3 rounded-full transition-all duration-300 ${
                    selectedEventType === type.id
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg transform -translate-y-1'
                      : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {type.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Events Portfolio */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Nos Créations d'Exception
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Chaque événement est unique, conçu pour dépasser vos rêves les plus ambitieux
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {filteredEvents.map((event) => (
              <Card key={event.id} className="overflow-hidden hover:shadow-2xl transition-all duration-500 group">
                <div className="relative h-80">
                  <img 
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/20 backdrop-blur-md border-white/30 text-white">
                      <Crown className="w-3 h-3 mr-1" />
                      Prestige
                    </Badge>
                  </div>

                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h3 
                      className="text-2xl font-light mb-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {event.title}
                    </h3>
                    <p className="text-sm opacity-90 mb-4">{event.subtitle}</p>
                    
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{event.guests}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{event.duration}</span>
                        </div>
                      </div>
                      <div className="text-lg font-light">
                        {event.price}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="flex items-center gap-2 mb-6">
                    <MapPin className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-gray-600">{event.location}</span>
                  </div>

                  <div className="space-y-3 mb-8">
                    <h4 className="font-medium text-gray-800">Prestations incluses :</h4>
                    {event.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <Star className="w-4 h-4 text-purple-600" />
                        <span className="text-sm text-gray-600">{highlight}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white transition-all duration-300"
                  >
                    Planifier cet Événement
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-900 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Services Complets
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Une équipe d'experts dédiés pour chaque aspect de votre événement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-purple-300" />
                  </div>
                  
                  <h3 className="text-lg font-light mb-4">{service.title}</h3>
                  <p className="text-purple-200 text-sm mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="space-y-2">
                    {service.details.map((detail, idx) => (
                      <div key={idx} className="text-xs text-purple-300">
                        • {detail}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Planning Process */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Processus de Création
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              De l'idée initiale à la réalisation parfaite de votre événement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Briefing Créatif", desc: "Échange approfondi sur votre vision et vos attentes" },
              { step: "2", title: "Conception", desc: "Création d'un concept unique et personnalisé" },
              { step: "3", title: "Coordination", desc: "Organisation détaillée avec tous les prestataires" },
              { step: "4", title: "Réalisation", desc: "Exécution parfaite le jour J avec supervision complète" }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-light text-white">{phase.step}</span>
                </div>
                <h3 className="text-xl font-light text-gray-800 mb-4">{phase.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Votre Événement de Rêve
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Confiez-nous votre célébration la plus importante. Nous transformerons 
            vos rêves en souvenirs inoubliables.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Planifier mon Événement
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              Portfolio Complet
            </Button>
          </div>

          <div className="bg-white rounded-lg p-6 max-w-md mx-auto">
            <p className="text-sm text-gray-600 mb-2">Consultation gratuite</p>
            <p className="text-2xl font-light text-purple-600">+1 786 840 0745</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;